import React, { useState, useEffect } from "react";
import { func, shape, arrayOf, string, bool } from "prop-types";
import CircleInfoSolidV6 from "@shared/v2/Icomoon/Icons/CircleInfoSolidV6";
import Tooltip from "@shared/v2/Tooltip";
import Checkbox from "@shared/v2/Checkbox/Checkbox";
import Dropdown from "@shared/v2/Dropdown/Dropdown";
import TextComboInput from "@shared/v2/TextComboInput";
import TextButton from "@shared/v2/TextButton";

const AddAdditionalContact = ({
  setNewContact,
  newContact,
  roleOptions,
  hideAddContactSection,
  onSaveContact,
  contactType,
  isEdit,
}) => {
  const [errors, setErrors] = useState({ email: "", phone: "" });

  useEffect(() => {
    const newcontact = newContact?.phone ? newContact?.phone.trim() : "";
    if (newcontact === "" && errors.phone) {
      setErrors((prev) => ({
        ...prev,
        phone: "",
      }));
    }
  }, [newContact?.phone]);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhone = (phone) => {
    // Covers formats like: (123) 456-7890 or 123-456-7890 or +1 123 456 7890 or 1234567890
    const regex = /^(\+1\s?)?(\(?\d{3}\)?[\s-]?)?\d{3}[\s-]?\d{4}$/;
    return regex.test(phone);
  };

  const handleContactChange = (field, value) => {
    if (field === "email") {
      setErrors((prev) => ({
        ...prev,
        email: validateEmail(value) ? "" : "Invalid email address",
      }));
    }
    if (field === "phone") {
      setErrors((prev) => ({
        ...prev,
        phone: validatePhone(value) ? "" : "Invalid phone number",
      }));
    }
    setNewContact((prev) => ({ ...prev, [field]: value }));
  };

  const onCancel = () => {
    setNewContact({});
    hideAddContactSection();
  };

  return (
    <div>
      {!isEdit && (
        <div className="tw-pb-[9px] tw-text-14px tw-leading-[20px] tw-font-semibold tw-text-gray-50">
          <span>
            {contactType === "co-op"
              ? "Cooperating Agent(s)"
              : contactType === "seller"
                ? "Seller(s)"
                : "Additional Contacts"}
          </span>
        </div>
      )}

      <div className="tw-border-solid tw-bg-[#FFFFFF] tw-border-neutral-gray-10 tw-w-[552px] tw-mb-[8px] !tw-p-[15px] tw-border tw-rounded-8px">
        {contactType !== "seller" && (
          <>
            <span className="tw-pr-[3px] tw-text-14px tw-leading-[20px] tw-font-normal tw-text-gray-50">
              This person will be added as a new Brivity contact and added to the transaction as a{" "}
              {contactType === "additional" ? "client or" : ""} collaborator.
            </span>
            <Tooltip
              innerClassName="tw-max-w-[180px]"
              trigger={<CircleInfoSolidV6 />}
              content={
                contactType === "co-op" ? (
                  <div className="tw-text-left tw-font-normal tw-leading-[16px] tw-text-[12px]">
                    This person will be added as a new Brivity contact with the following info:
                    <ul className="tw-m-0 tw-p-0 tw-pl-[14px]">
                      <li>Source: Transaction Collaborator</li>
                      <li>Intention: None</li>
                      <li>Person type: Collaborator </li>
                    </ul>
                  </div>
                ) : (
                  <div className="tw-text-left tw-font-normal tw-leading-[16px] tw-text-[12px]">
                    Collaborator roles will be added as a new Brivity contact with
                    <ul className="tw-m-0 tw-p-0 tw-pl-[14px]">
                      <li>Source: Transaction Collaborator</li>
                      <li>Person type: Collaborator</li>
                    </ul>
                    <div className="tw-pt-[16px]">Client roles will be added a with:</div>
                    <ul className="tw-m-0 tw-p-0 tw-pl-[14px]">
                      <li>Source: Transaction Client</li>
                      <li>Person Type: Lead</li>
                    </ul>
                  </div>
                )
              }
            />
          </>
        )}

        <div
          className={`${contactType !== "seller" && "tw-pt-[16px]"} tw-grid tw-grid-cols-2 tw-gap-[16px] tw-relative`}
        >
          <TextComboInput
            className="tw-z-50"
            label="First Name"
            isRequired
            containerClassName="tw-col-span-1"
            data-cy="first_name"
            value={newContact?.firstName || ""}
            onChange={(e) => handleContactChange("firstName", e.target.value)}
          />
          <TextComboInput
            className="tw-z-50"
            label="Last Name"
            isRequired
            containerClassName="ttw-col-span-1"
            data-cy="last_name"
            value={newContact?.lastName || ""}
            onChange={(e) => handleContactChange("lastName", e.target.value)}
          />
        </div>

        <div className="tw-pt-[16px] tw-grid tw-grid-cols-2 tw-gap-[16px] tw-relative">
          <TextComboInput
            className="tw-z-50"
            label="Email"
            isRequired
            error={errors.email}
            containerClassName="tw-col-span-1"
            data-cy="email"
            value={newContact?.email || ""}
            onChange={(e) => handleContactChange("email", e.target.value)}
          />
          <TextComboInput
            className="tw-z-50"
            label="Phone Number"
            error={errors.phone}
            containerClassName="ttw-col-span-1"
            data-cy="phone"
            value={newContact?.phone || ""}
            onChange={(e) => handleContactChange("phone", e.target.value)}
          />
        </div>
        <div className="tw-pt-[16px] tw-grid tw-grid-cols-2 tw-gap-[16px] tw-relative">
          <div className="tw-col-span-2">
            <Dropdown
              isSearchable
              isRequired
              label="Transaction Role"
              menuShouldComeToFront
              maxVisibleOptions={7.5}
              options={roleOptions}
              name="transaction_role"
              value={newContact?.role || null}
              onChange={(value) => handleContactChange("role", value)}
            />
          </div>
        </div>
        <div className="tw-pt-[16px] tw-grid tw-grid-cols-2 tw-gap-[16px] tw-relative">
          <div className="tw-col-span-2">
            <Checkbox
              onChange={(e) => handleContactChange("addToTransaction", e.target.checked)}
              label="Grant viewer login access to client portal"
              checked={newContact?.addToTransaction}
            />
            <span className="tw-pl-[6px]">
              <Tooltip
                innerClassName="tw-max-w-[180px]"
                trigger={<CircleInfoSolidV6 />}
                content={
                  <div className="tw-text-left tw-font-normal tw-leading-[16px] tw-text-[12px]">
                    This person will be invited to access a limited view of this transaction.
                  </div>
                }
              />
            </span>
          </div>
        </div>
        <div className="tw-pt-[16px] tw-flex tw-justify-between">
          <TextButton size="medium" schema="remove" className="tw-pl-[4px]" onClick={onCancel}>
            Cancel
          </TextButton>
          <TextButton
            disabled={
              !newContact?.firstName ||
              !newContact?.lastName ||
              !newContact?.email ||
              !newContact?.role ||
              errors.email ||
              errors.phone
            }
            size="medium"
            className="tw-pl-[4px]"
            onClick={onSaveContact}
          >
            Done
          </TextButton>
        </div>
      </div>
    </div>
  );
};

AddAdditionalContact.propTypes = {
  setNewContact: func,
  newContact: shape({}),
  roleOptions: arrayOf(shape({})),
  hideAddContactSection: func,
  onSaveContact: func,
  contactType: string,
  isEdit: bool,
};

AddAdditionalContact.defaultProps = {
  setNewContact: null,
  newContact: null,
  roleOptions: [],
  hideAddContactSection: null,
  onSaveContact: null,
  contactType: "co-op",
  isEdit: false,
};

export default AddAdditionalContact;
